/**
 * ResetPassword — pantalla que abre el usuario al hacer click en el link del correo.
 * Valida el token al cargar; si es válido, muestra el form para nueva contraseña.
 * Mismo estilo visual que Login (gradient border + branding).
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader2, CheckCircle, AlertTriangle, ArrowLeft, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { authApi } from '../utils/api';
import MarcoAcceso from '../components/MarcoAcceso';
import { useTextosAcceso } from '../utils/idiomaAcceso';

function getTenantSlug() {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  if (parts.length > 2 || (parts.length === 2 && parts[1] === 'localhost')) return parts[0];
  return localStorage.getItem('tenantSlug') || 'ews';
}

export default function ResetPassword() {
  const { token } = useParams();
  const t = useTextosAcceso();
  const navigate = useNavigate();
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authApi.validateResetToken(token, getTenantSlug());
        if (cancelled) return;
        if (res.data?.valid) {
          setValid(true);
          setEmail(res.data.email || '');
        } else {
          setValid(false);
        }
      } catch {
        if (!cancelled) setValid(false);
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error(t.minimo); return; }
    if (password !== confirm) { toast.error(t.noCoinciden); return; }
    setSubmitting(true);
    try {
      await authApi.resetPassword(token, password, getTenantSlug());
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(err.response?.data?.error || t.errorRestablecer);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarcoAcceso>
            {validating && (
              <div className="text-center py-6">
                <Loader2 className="h-10 w-10 text-slate-600 animate-spin mx-auto mb-3" />
                <p className="text-sm text-gray-600">{t.validando}</p>
              </div>
            )}

            {!validating && !valid && (
              <div className="text-center py-2">
                <div className="inline-flex p-3 rounded-full bg-red-100 mb-4">
                  <AlertTriangle className="h-9 w-9 text-red-600" />
                </div>
                <h2>{t.invalido}</h2>
                <p className="text-sm text-gray-600">{t.invalidoTexto}</p>
                <div className="mt-6 flex flex-col gap-3">
                  <Link
                    to="/forgot-password"
                    className="boton"
                  >
                    {t.pedirOtro}
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                  <Link to="/login" className="olvido inline-flex items-center justify-center gap-1.5">
                    <ArrowLeft className="h-3.5 w-3.5" />
                    {t.volver}
                  </Link>
                </div>
              </div>
            )}

            {!validating && valid && !done && (
              <>
                <div className="mb-6">
                  <h2>{t.nueva}</h2>
                  <p className="bajada" style={{ marginBottom: 0 }}>{t.para} <strong>{email}</strong></p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label>{t.nueva}</label>
                    <div className="relative">
                      <input
                        type={showPwd ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoFocus
                        autoComplete="new-password"
                        placeholder={t.minimo}
                        className="campo" style={{ paddingRight: 44 }}
                      />
                      <button type="button" onClick={() => setShowPwd(p => !p)} className="ver" aria-label={t.mostrarOcultar}>
                        {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label>{t.confirmar}</label>
                    <input
                      type={showPwd ? 'text' : 'password'}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      autoComplete="new-password"
                      placeholder={t.escribaDeNuevo}
                      className="campo"
                    />
                    {confirm && password !== confirm && (
                      <p className="text-xs text-red-600 mt-1">{t.noCoinciden}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || password.length < 8 || password !== confirm}
                    className="boton"
                  >
                    {submitting ? (
                      <>
                        <span className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t.guardando}
                      </>
                    ) : (
                      <>
                        {t.guardar}
                        <Lock className="h-5 w-5" />
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <Link to="/login" className="olvido inline-flex items-center gap-1.5">
                    <ArrowLeft className="h-3.5 w-3.5" />
                    {t.volver}
                  </Link>
                </div>
              </>
            )}

            {done && (
              <div className="text-center py-4">
                <div className="inline-flex p-3 rounded-full bg-emerald-100 mb-4">
                  <CheckCircle className="h-9 w-9 text-emerald-600" />
                </div>
                <h2>{t.listo}</h2>
                <p className="text-sm text-gray-600">{t.redirigiendo}</p>
              </div>
            )}
    </MarcoAcceso>
  );
}
